import { createAsyncThunk, createAction } from "@reduxjs/toolkit";
import { awsClientGateway } from "./common/gateway";
import { getInstanceId } from "./common/serviceUtils";
import { logMessage } from "../utils/amplifyLogger";
import { clientLog } from "./logging";
import type { RootState } from "../store/types";
import type {
  LexBotInfo,
  LexIntentSummary,
  LexSlotSummary,
  LexSlotTypeSummary,
  LexSlotTypeDetailData,
  LexMissedUtteranceCount,
} from "../features/content/manager/table/lexBots/types";

// Payload types
export interface LexIntentsPayload {
  botId: string;
  botVersion: string;
  localeId: string;
}

export interface LexUtterancesPayload {
  botId: string;
  botVersion: string;
  localeId: string;
  intentId: string;
}

export interface LexSlotsPayload {
  botId: string;
  botVersion: string;
  localeId: string;
  intentId: string;
}

export interface LexSlotTypesPayload {
  botId: string;
  botVersion: string;
  localeId: string;
}

export interface LexSlotTypeDetailPayload {
  botId: string;
  botVersion: string;
  localeId: string;
  slotTypeId: string;
}

export interface LexMissedUtterancesPayload {
  botId: string;
  botAliasId: string;
  localeId: string;
  botVersion: string;
}

// Response envelope shapes returned by awsClientGateway
export interface LexBotsResponse {
  data?: { bots?: LexBotInfo[] };
}
export interface LexIntentsResponse {
  data?: { intentSummaries?: LexIntentSummary[] };
}
export interface LexUtterancesResponse {
  data?: { utterances?: string[] };
}
export interface LexSlotsResponse {
  data?: { slotSummaries?: LexSlotSummary[] };
}
export interface LexSlotTypesResponse {
  data?: { slotTypeSummaries?: LexSlotTypeSummary[] };
}
export interface LexSlotTypeDetailResponse {
  data?: LexSlotTypeDetailData;
}
export interface LexMissedUtterancesResponse {
  data?: { missedUtterances?: LexMissedUtteranceCount[] };
}

// Shared helper
const lexPost = async <T extends Record<string, string>>(
  instanceId: string,
  apiName: string,
  body: T,
) => {
  return awsClientGateway({
    pathTemplate: `/lexApis/${instanceId}/${apiName}`,
    method: "POST",
    body,
  });
};

const logError = (apiName: string, error: unknown) => {
  clientLog({
    component: "Lex Bots",
    message: `Error in ${apiName}: ${error}`,
  });
  logMessage("lexBots", `Error in ${apiName}: ${error}`, "error");
};

// 1. lex-list-bots
export const getLexBots = createAsyncThunk<
  LexBotsResponse,
  void,
  { state: RootState; rejectValue: string }
>("lexBots/getLexBots", async (_arg, { getState, rejectWithValue }) => {
  const instanceId = getInstanceId(getState());
  try {
    return await lexPost(instanceId, "lex-list-bots", {});
  } catch (error) {
    logError("lex-list-bots", error);
    return rejectWithValue(`Error fetching Lex bots: ${error}`);
  }
});

// 2. lex-list-intents
export const getLexIntents = createAsyncThunk<
  LexIntentsResponse,
  LexIntentsPayload,
  { state: RootState; rejectValue: string }
>("lexBots/getLexIntents", async (payload, { getState, rejectWithValue }) => {
  const instanceId = getInstanceId(getState());
  try {
    return await lexPost(instanceId, "lex-list-intents", payload);
  } catch (error) {
    logError("lex-list-intents", error);
    return rejectWithValue(`Error fetching Lex intents: ${error}`);
  }
});

// 3. lex-get-utterances
export const getLexUtterances = createAsyncThunk<
  LexUtterancesResponse,
  LexUtterancesPayload,
  { state: RootState; rejectValue: string }
>(
  "lexBots/getLexUtterances",
  async (payload, { getState, rejectWithValue }) => {
    const instanceId = getInstanceId(getState());
    try {
      return await lexPost(instanceId, "lex-get-utterances", payload);
    } catch (error) {
      logError("lex-get-utterances", error);
      return rejectWithValue(`Error fetching Lex utterances: ${error}`);
    }
  },
);

// 4. lex-list-intent-slots
export const getLexSlots = createAsyncThunk<
  LexSlotsResponse,
  LexSlotsPayload,
  { state: RootState; rejectValue: string }
>("lexBots/getLexSlots", async (payload, { getState, rejectWithValue }) => {
  const instanceId = getInstanceId(getState());
  try {
    return await lexPost(instanceId, "lex-list-intent-slots", payload);
  } catch (error) {
    logError("lex-list-intent-slots", error);
    return rejectWithValue(`Error fetching Lex slots: ${error}`);
  }
});

// 5. lex-list-slot-types
export const getLexSlotTypes = createAsyncThunk<
  LexSlotTypesResponse,
  LexSlotTypesPayload,
  { state: RootState; rejectValue: string }
>("lexBots/getLexSlotTypes", async (payload, { getState, rejectWithValue }) => {
  const instanceId = getInstanceId(getState());
  try {
    return await lexPost(instanceId, "lex-list-slot-types", payload);
  } catch (error) {
    logError("lex-list-slot-types", error);
    return rejectWithValue(`Error fetching Lex slot types: ${error}`);
  }
});

// 6. lex-describe-slot-type
export const getLexSlotTypeDetail = createAsyncThunk<
  LexSlotTypeDetailResponse,
  LexSlotTypeDetailPayload,
  { state: RootState; rejectValue: string }
>(
  "lexBots/getLexSlotTypeDetail",
  async (payload, { getState, rejectWithValue }) => {
    const instanceId = getInstanceId(getState());
    try {
      return await lexPost(instanceId, "lex-describe-slot-type", payload);
    } catch (error) {
      logError("lex-describe-slot-type", error);
      return rejectWithValue(`Error fetching Lex slot type detail: ${error}`);
    }
  },
);

// 7. lex-missed-utterances
export const getLexMissedUtterances = createAsyncThunk<
  LexMissedUtterancesResponse,
  LexMissedUtterancesPayload,
  { state: RootState; rejectValue: string }
>(
  "lexBots/getLexMissedUtterances",
  async (payload, { getState, rejectWithValue }) => {
    const instanceId = getInstanceId(getState());
    try {
      return await lexPost(instanceId, "lex-missed-utterances", payload);
    } catch (error) {
      logError("lex-missed-utterances", error);
      return rejectWithValue(`Error fetching Lex missed utterances: ${error}`);
    }
  },
);

/**
 * Reset actions
 * Clears bot-detail data only (intents, utterances, slots, slotTypes, slotTypeDetail, missedUtterances)
 * Does NOT clear the bots list
 */
export const resetLexBotDetail = createAction("lexBots/resetLexBotDetail");

// Clears all Lex state including the bots list.
export const resetLexBots = createAction("lexBots/resetLexBots");
